import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { providerTypeLabel } from "@alm/domain";
import type { RouterOutputs } from "@alm/api-contracts";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { searchPatch, optionalSearch } from "@/lib/search";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import { DirectoryPage, type DirectoryColumn } from "./-components/directory-page";
import { useReferralSourceColumn } from "./-components/referral-source-column";
import { PortalLoginCell } from "./-components/portal-login-cell";
import { facilityInput, ProviderForm, type ProviderValues } from "./-components/directory-forms";
import { facilityValue } from "@/routes/_authenticated/_care/-components/directory-forms-shared";
import { keepWhileSame } from "@/lib/query/keep-while-same";

export const Route = createFileRoute("/_authenticated/_care/providers")({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: optionalSearch(search.q),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "providers"),
  component: ProvidersPage,
});

type Row = RouterOutputs["care"]["directories"]["providers"]["list"][number];

const COLUMNS: ReadonlyArray<DirectoryColumn<Row>> = [
  {
    header: "Provider",
    cell: (row) => (
      <span className="font-medium">
        {row.lastName}, {row.firstName}
        {row.credentials ? ` ${row.credentials}` : ""}
      </span>
    ),
    clamp: "md",
  },
  { header: "Type", cell: (row) => providerTypeLabel(row.providerType) },
  { header: "Specialty", priority: 2, cell: (row) => row.specialty ?? "-", clamp: "sm" },
  { header: "Practice", priority: 3, cell: (row) => row.practiceName ?? "-", clamp: "md" },
  { header: "Phone", priority: 2, cell: (row) => row.phone ?? "-" },
  { header: "Email", priority: 3, cell: (row) => row.email ?? "-", clamp: "md", lines: 1 },
  { header: "Login", cell: (row) => <PortalLoginCell login={row.login} provider={row} /> },
];

function ProvidersPage() {
  const { facilityId } = useFacility();
  const { q } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(q, (next) => patch({ q: next }));
  const list = useQuery({
    ...orpc.care.directories.providers.list.queryOptions({
      input: { facilityId, search: q || undefined, includeInactive: true },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const referral = useReferralSourceColumn("provider", list.data);
  const columns = referral ? [...COLUMNS, referral] : COLUMNS;
  return (
    <DirectoryPage<Row, ProviderValues>
      noun="Provider"
      title="Provider directory"
      meta="Doctors and other clinicians the building refers to."
      columns={columns}
      rows={list.data}
      isPending={list.isPending}
      error={list.error}
      search={search}
      onSearch={setSearch}
      searching={settling || list.isPlaceholderData}
      create={(values) =>
        orpcClient.care.directories.providers.create({
          ...values,
          facilityId: facilityInput(values.facilityId),
        })
      }
      update={(row, values) =>
        orpcClient.care.directories.providers.update({ providerId: row.id, ...values })
      }
      remove={(row) => orpcClient.care.directories.providers.remove({ providerId: row.id })}
      toValues={(row) => ({
        ...row,
        facilityId: facilityValue(row.facilityId),
        providerType: row.providerType as ProviderValues["providerType"],
      })}
      renderForm={(args) => <ProviderForm {...args} />}
    />
  );
}
